jest.mock('../StepContainer');

import { StepContainerManager } from './StepContainerManager';
import { StepConfig } from '../../../models';
// import { ConfigTreeNode } from "./models";

import { PackageMemoryCache } from '../Repository/PackageMemoryCache';
import { StepContainer } from '../StepContainer';
import { mock, MockProxy } from 'jest-mock-extended';
import { Logger, StepEvent, StepPackage } from '@samelogic/steps';
import { stepPackage } from '../../__tests__/MockPackage';
import { contextFactory, contextMetadataFactory } from '../../__tests__/factories';
import { Context } from '../../Context/Context';
import { SessionManager } from '../../../tracking';
import { EventLogger } from '../../../tracking/EventLogger';

const mockedStepContainer = <jest.Mock<StepContainer>>StepContainer;

beforeEach(() => {
  mockedStepContainer.mockClear();
});

describe('StepContainerManager', () => {
  // type CaseEvent = {
  //   /**
  //    * The id of the step node instance that fired the event
  //    */
  //   instanceId: string;
  //   event: string;

  //   expectedCreatedContainer: {
  //     parentId: string;
  //     config: ConfigTreeNode;
  //   };
  // };
  type ConfigSet = {
    config: StepConfig;
    package: StepPackage;
    expectedContext: Context;
    firedEvents: StepEvent[];
  };
  type Case = {
    name: string;
    configs: Map<string, ConfigSet>;
    // events: CaseEvent[];
  };

  /*
    three layer case
           ┌──────┐
           │ root │
           └──┬───┘
              │
        ┌─────┴─────┐
        │           │
      eventA      eventB
        │           │
      ┌─┴─┐       ┌─┴─┐
      │ A │       │ B │
      └─┬─┘       └───┘
        │
  ┌─────┴────┐
  │          │
eventA    eventA
  │          │
┌─┴──┐     ┌─┴──┐
│ A1 │     │ A2 │
└────┘     └────┘
 */

  const context = new Context(new SessionManager('env-id'), contextMetadataFactory.build(), {});
  const threeLayerSuccessCase: Case = {
    name: 'when workflow is executed with all matching schemas',
    configs: new Map([
      [
        'root',
        {
          config: {
            name: 'root',
            uses: ''
          },
          package: stepPackage,
          expectedContext: context,
          firedEvents: [
            {
              stepName: 'root',
              event: 'eventA',
              data: {
                test: 'test-1'
              }
            },
            {
              stepName: 'root',
              event: 'eventB',
              data: {
                num: 1
              }
            }
          ]
        }
      ],
      [
        'a',
        {
          config: {
            name: 'a',
            uses: '',
            needs: {
              name: 'root',
              event: 'eventA'
            },
            props: {
              'hard-coded-prop': 'hard-coded',
              'mapped-prop': '${events.root.eventA.data.test}'
            }
          },
          package: stepPackage,
          expectedContext: context.clone({ root: { eventA: { data: { test: 'test-1' } } } }),
          firedEvents: [
            {
              stepName: 'a',
              event: 'eventA',
              data: {
                a: 'a1'
              }
            },
            {
              stepName: 'a',
              event: 'eventA',
              data: {
                a: 'a2'
              }
            }
          ]
        }
      ],
      [
        'a1',
        {
          config: {
            name: 'a1',
            uses: '',
            needs: {
              name: 'a',
              event: 'eventA'
            }
          },
          package: stepPackage,
          expectedRunProps: null,
          expectedContext: context.clone({
            root: { eventA: { data: { test: 'test-1' } } },
            a: { eventA: { data: { a: 'a1' } } }
          }),
          firedEvents: []
        }
      ],
      [
        'a2',
        {
          config: {
            name: 'a1',
            uses: '',
            needs: {
              name: 'a',
              event: 'eventA'
            }
          },
          package: stepPackage,
          expectedContext: contextFactory.build({
            events: { root: { eventA: { data: { test: 'test-1' } } }, a: { eventA: { data: { a: 'a2' } } } }
          }),
          firedEvents: []
        }
      ],
      [
        'b',
        {
          config: {
            name: 'b',
            uses: '',
            needs: {
              name: 'root',
              event: 'eventB'
            }
          },
          package: stepPackage,
          expectedContext: contextFactory.build({
            events: { root: { eventB: { data: { num: 1 } } } }
          }),
          firedEvents: []
        }
      ]
    ])
  };

  describe.each([threeLayerSuccessCase])('$name', ({ configs }) => {
    let loggerMock: MockProxy<Logger>;
    let packageCacheMock: MockProxy<PackageMemoryCache>;
    let eventLoggerMock: MockProxy<EventLogger>;

    beforeEach(() => {
      loggerMock = mock<Logger>();
      packageCacheMock = mock<PackageMemoryCache>();
      eventLoggerMock = mock<EventLogger>();
    });

    it('should execute all steps with the right props', async () => {
      const allConfigs = Array.from(configs, ([_key, value]) => value.config);
      const mockContainers = new Map<string, StepContainer>();
      for (const config of allConfigs) {
        mockContainers.set(config.name, mock<StepContainer>());
      }
      const _tmpContexts = new Array<Context>();
      mockedStepContainer.mockImplementation((_ctx, _pkg, cfg: StepConfig) => {
        _tmpContexts.push(_ctx as Context);
        const container = mockContainers.get(cfg.name);
        if (container == null) {
          throw Error('cannot find mock container');
        }
        return container;
      });

      packageCacheMock.get.mockResolvedValue(stepPackage);

      const manager = new StepContainerManager(allConfigs, packageCacheMock, loggerMock, eventLoggerMock);

      const checkStepRan = (stepName: string) => {
        const runningconfig = configs.get(stepName);
        if (runningconfig === undefined) {
          throw new Error(`Cannot find step: ${stepName}`);
        }

        expect(mockedStepContainer).toHaveBeenCalledWith(
          runningconfig.expectedContext,
          expect.any(Object),
          runningconfig.config,
          loggerMock
        );
        const mockContainer = mockContainers.get(runningconfig.config.name);
        if (mockContainer == null) {
          throw Error('cannot find mock container');
        }
        expect(mockContainer.run).toHaveBeenCalled();
      };
      const triggerEventForStep = async (stepName: string) => {
        const runningconfig = configs.get(stepName);
        if (runningconfig === undefined) {
          throw new Error(`Cannot find step ${stepName}`);
        }

        const mockContainer = mockContainers.get(runningconfig.config.name);
        if (mockContainer == null) {
          throw Error('cannot find mock container');
        }
        for (const event of runningconfig.firedEvents) {
          await mockContainer.onEventTriggered?.(event);
        }
      };

      await manager.start(context);
      // check first step
      checkStepRan('root');

      await triggerEventForStep('root');
      checkStepRan('a');
      // checkStepRan('b');

      // await triggerEventForStep('a');
      // checkStepRan('a1');
      // checkStepRan('a2');
    });
  });
});
